// Rich demo fixture for the scan prioritization tab (nee finding overlays). Seeds `dev:mocks` so
// opening the tab in a browser shows a realistic, multi-tier prioritization without regenerating.
import type { FindingOverlayResponse } from '../src/utils/api-client/api-schemas'

const p = (
  findingId: string,
  tool: string,
  rule: string,
  path: string,
  severity: string,
  tier: 'critical' | 'high' | 'medium' | 'low',
  score: number,
  rationale: string
): FindingOverlayResponse['priorities'][number] => ({
  findingId,
  tool,
  rule,
  path,
  severity,
  tier,
  score,
  rationale,
  _links: {
    self: { href: `/api/v1/scans/scan-1/findings/${findingId}` },
    finding: { href: `/api/v1/scans/scan-1/findings/${findingId}` },
  },
})

const chip = (findingId: string, tool: string, rule: string, path: string, severity: string) => ({
  findingId,
  tool,
  rule,
  path,
  severity,
  _links: {
    self: { href: `/api/v1/scans/scan-1/findings/${findingId}` },
    finding: { href: `/api/v1/scans/scan-1/findings/${findingId}` },
  },
})

export const EXAMPLE_SCAN_PRIORITIZATION_CONTENT: FindingOverlayResponse = {
  narrative:
    'analysis-service concentrates its exposure at the **ingest boundary** — the presigned-URL fetcher, the archive extractor, and the LLM-provider outbound calls.\n\n- Two **critical** issues sit on the ingest path (SQL injection in order lookup, and a weak JWT secret used by the token issuer) and cover most of the blast radius.\n- Three **high** issues are variations of the same theme: user-controlled data reaching sensitive sinks without canonicalisation.\n- Everything else is either policy-layer (framing headers, TLS ciphers) or ambient (stack traces in error responses).',
  priorities: [
    p(
      'codeql:py/sql-injection@src/orders/lookup.py:42',
      'codeql',
      'sql-injection',
      'src/orders/lookup.py',
      'critical',
      'critical',
      92,
      'Untrusted URL segment is interpolated into a SQL query on the anonymous-reachable order-lookup endpoint. Reachable in one hop; the underlying database is the primary customer datastore.'
    ),
    p(
      'sonar:py/weak-jwt-secret@src/auth/token.py:14',
      'sonar',
      'weak-jwt-secret',
      'src/auth/token.py',
      'critical',
      'critical',
      88,
      'The JWT signing secret is short (10 chars) and reused across environments. Two independent findings converge — one SAST, one SCA advisory — and the token-issuer path is used by every authenticated endpoint.'
    ),
    p(
      'semgrep:py/ssrf@src/proxy/fetch.py:22',
      'semgrep',
      'ssrf',
      'src/proxy/fetch.py',
      'high',
      'high',
      78,
      'User-supplied URL is fetched without host-allowlisting. The service runs in a VPC with metadata service exposure, so a successful SSRF would leak instance credentials.'
    ),
    p(
      'codeql:py/path-traversal@src/upload/zip.py:31',
      'codeql',
      'path-traversal',
      'src/upload/zip.py',
      'high',
      'high',
      74,
      'Archive extraction does not validate entry names against a canonicalised destination root. A crafted archive can write outside the sandbox and clobber code on the next start.'
    ),
    p(
      'checkmarx:py/hardcoded-credentials@src/aws/client.py:9',
      'checkmarx',
      'hardcoded-credentials',
      'src/aws/client.py',
      'high',
      'high',
      71,
      'AWS access key committed to source in a fallback path. Guarded by a feature flag today, but the flag has been observed toggled on in staging.'
    ),
    p(
      'semgrep:py/weak-hash@src/pw/hash.py:12',
      'semgrep',
      'weak-hash',
      'src/pw/hash.py',
      'medium',
      'medium',
      58,
      'Legacy password hashing uses MD5 with a static salt. Only a migration table (roughly 3% of users) still relies on it; a rehash-on-login is already scheduled.'
    ),
    p(
      'codeql:py/xss-reflected@src/render/msg.py:44',
      'codeql',
      'xss-reflected',
      'src/render/msg.py',
      'medium',
      'medium',
      55,
      'Reflected XSS in an internal admin surface. Behind SSO; a successful exploit would need a phished admin session.'
    ),
    p(
      'sonar:py/insecure-deserialization@src/queue/consumer.py:88',
      'sonar',
      'insecure-deserialization',
      'src/queue/consumer.py',
      'medium',
      'medium',
      52,
      'Pickle-based message deserialization on the internal work-queue consumer. Producer-side messages are typed, but there is no message-level auth to prevent a malicious producer from injecting a crafted payload.'
    ),
    p(
      'sonar:py/no-x-frame-options@src/http/headers.py:5',
      'sonar',
      'no-x-frame-options',
      'src/http/headers.py',
      'low',
      'low',
      32,
      'Framing headers are not set on the admin surface. Clickjacking risk is bounded by SSO gating; kept in the queue for policy compliance.'
    ),
    p(
      'checkmarx:py/weak-tls-cipher@src/net/tls.py:18',
      'checkmarx',
      'weak-tls-cipher',
      'src/net/tls.py',
      'low',
      'low',
      28,
      'A legacy cipher suite remains enabled in the outbound TLS config for a partner integration. Partner has committed to migrating in Q4.'
    ),
    p(
      'semgrep:py/exposed-stack-trace@src/errors/render.py:23',
      'semgrep',
      'exposed-stack-trace',
      'src/errors/render.py',
      'low',
      'low',
      22,
      'Stack traces returned to callers on unhandled 500s. Reveals path structure; no secrets observed in the observed traces, but grade-B hygiene.'
    ),
  ],
  clusters: [
    {
      name: 'Order lookup endpoint',
      dimension: 'entry_point',
      subjectName: '/api/v1/orders/{id}',
      rationale:
        'Untrusted URL segment reaches a SQL query on the same handler that renders errors with raw templating. Fixing input validation at the entry point deprioritises both findings below at once.',
      findings: [
        chip(
          'codeql:py/sql-injection@src/orders/lookup.py:42',
          'codeql',
          'sql-injection',
          'src/orders/lookup.py',
          'critical'
        ),
        chip(
          'semgrep:py/exposed-stack-trace@src/errors/render.py:23',
          'semgrep',
          'exposed-stack-trace',
          'src/errors/render.py',
          'low'
        ),
      ],
    },
    {
      name: 'Archive fetcher',
      dimension: 'trust_boundary',
      subjectName: 'workflow.task_queue',
      rationale:
        'The presigned-URL fetcher extracts into a temp directory without canonicalising entry names against the destination root. A crafted archive can write outside the sandbox.',
      findings: [
        chip('codeql:py/path-traversal@src/upload/zip.py:31', 'codeql', 'path-traversal', 'src/upload/zip.py', 'high'),
        chip(
          'sonar:py/insecure-deserialization@src/queue/consumer.py:88',
          'sonar',
          'insecure-deserialization',
          'src/queue/consumer.py',
          'medium'
        ),
      ],
    },
    {
      name: 'JWT authoring',
      dimension: 'sensitive_asset',
      subjectName: 'auth.token',
      rationale:
        'The signing secret is short and reused across environments; two independent findings flag it, one from the SAST tool and one from the SCA advisory feed.',
      findings: [
        chip(
          'sonar:py/weak-jwt-secret@src/auth/token.py:14',
          'sonar',
          'weak-jwt-secret',
          'src/auth/token.py',
          'critical'
        ),
        chip('semgrep:py/weak-hash@src/pw/hash.py:12', 'semgrep', 'weak-hash', 'src/pw/hash.py', 'medium'),
      ],
    },
    {
      name: 'Anonymous callers',
      dimension: 'actor',
      subjectName: 'anonymous',
      rationale:
        'Anonymous callers reach the health endpoint and, by mistake, one debug endpoint that echoes internal state. Two other unauthenticated findings converge here.',
      findings: [
        chip('semgrep:py/ssrf@src/proxy/fetch.py:22', 'semgrep', 'ssrf', 'src/proxy/fetch.py', 'high'),
        chip(
          'semgrep:py/exposed-stack-trace@src/errors/render.py:23',
          'semgrep',
          'exposed-stack-trace',
          'src/errors/render.py',
          'low'
        ),
      ],
    },
  ],
  assumptions: [
    "The threat model's understanding of the trust boundary is accepted as-is; findings on the anonymous surface are treated as more severe than the same finding on an authenticated route.",
    'Backreferences to the underlying scan and finding were resolved for 27 of 28 embedded references; one unresolved reference is rendered as an unlinked findingId label.',
    'Severity is inherited from the source tool where available; the prioritization does not attempt to re-rate CVSS.',
  ],
}
